/**
 * FteDemandBreakdown.tsx
 * Mockup C (2026-07-30, qa/fte-demand-mockups-2026-07-29): the expand-on-click
 * arithmetic behind the Phase-3 "Rough demand" strip — every measured line
 * (test × qty × per-test minutes → hours), the cost-estimated bucket tagged
 * "awaiting lab minutes", the excluded people-effort lines with their reason,
 * and the total. Rendered by FteDemandStrip when its chevron is open.
 */

import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { HybridFteDemand } from '../../../../services/scheduleFteConfig';

const fmtMoney = (n: number): string => `$${Math.round(n).toLocaleString('en-CA')}`;

const Dot: React.FC<{ color: string; dashed?: boolean }> = ({ color, dashed }) => (
  <Box
    component="span"
    sx={{
      width: 8, height: 8, borderRadius: '2px', flexShrink: 0,
      backgroundColor: dashed ? 'transparent' : color,
      border: dashed ? `1px dashed ${color}` : 'none',
    }}
  />
);

export const FteDemandBreakdown: React.FC<{ demand: HybridFteDemand }> = ({ demand }) => {
  const theme = useTheme();
  const measured = theme.schedule.committed;
  const estimated = theme.schedule.machineTime;
  const residualLines = demand.residualLines ?? [];
  const excludedLines = demand.excludedLines ?? [];

  const cellSx = { py: 0.5, px: 1.25, fontSize: '0.74rem', borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}` } as const;
  const headSx = {
    ...cellSx, py: 0.6, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5,
    textTransform: 'uppercase' as const, color: 'text.secondary', backgroundColor: theme.table.headerBg,
  };
  const numSx = { ...cellSx, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };

  return (
    <Box sx={{ mx: 1.5, mt: 0.5, border: `1px solid ${theme.border.color}`, borderRadius: '8px', overflow: 'hidden', backgroundColor: theme.palette.background.paper }}>
      <Table size="small" sx={{ '& td, & th': { border: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Line</TableCell>
            <TableCell sx={{ ...headSx, textAlign: 'right' }}>Qty</TableCell>
            <TableCell sx={{ ...headSx, textAlign: 'right' }}>Per test</TableCell>
            <TableCell sx={{ ...headSx, textAlign: 'right' }}>Hours</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {demand.bakedLines.map((l) => (
            <TableRow key={l.quoteLineItemKey}>
              <TableCell sx={cellSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Dot color={measured} />
                  {l.testName}
                  {/* Stage arithmetic (2026-07-30): the lab's measured minutes
                      per stage — sums to the Per-test column. */}
                  {l.stageMinutes && (
                    <Typography component="span" sx={{ fontSize: '0.66rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {[
                        l.stageMinutes.prep > 0 ? `prep ${l.stageMinutes.prep}` : '',
                        `test ${l.stageMinutes.test}`,
                        `report ${l.stageMinutes.report}`,
                        `review ${l.stageMinutes.review}`,
                        `approve ${l.stageMinutes.approve}`,
                      ].filter(Boolean).join(' + ')}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={numSx}>{l.quantity}</TableCell>
              <TableCell sx={numSx}>{l.minutesPerTest} min</TableCell>
              <TableCell sx={numSx}>{l.personHours}</TableCell>
            </TableRow>
          ))}

          {demand.residualPersonHours > 0 && (
            <TableRow>
              <TableCell sx={cellSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Dot color={estimated} />
                  {residualLines.length > 0
                    ? residualLines.map((l) => `${l.testName} ×${l.quantity}`).join(' + ')
                    : 'Unprofiled lines'}
                  {' — '}{fmtMoney(demand.residualValueCad)} ÷ ${demand.hourlyRate}/h
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.6rem', fontWeight: 700, color: estimated,
                      backgroundColor: alpha(estimated, 0.1), border: `1px solid ${alpha(estimated, 0.35)}`,
                      borderRadius: '999px', px: 0.75, py: 0.1,
                    }}
                  >
                    awaiting lab minutes
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={numSx}>{residualLines.reduce((s, l) => s + l.quantity, 0) || '—'}</TableCell>
              <TableCell sx={numSx}>—</TableCell>
              <TableCell sx={numSx}>{demand.residualPersonHours}</TableCell>
            </TableRow>
          )}

          {excludedLines.map((l) => (
            <TableRow key={l.quoteLineItemKey}>
              <TableCell sx={{ ...cellSx, color: 'text.disabled' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Dot color={theme.schedule.noActivity} dashed />
                  {l.testName} ({fmtMoney(l.lineValueCad)}) — staffed by hand in Team capacity
                </Box>
              </TableCell>
              <TableCell sx={{ ...numSx, color: 'text.disabled' }}>{l.quantity}</TableCell>
              <TableCell sx={{ ...numSx, color: 'text.disabled' }}>—</TableCell>
              <TableCell sx={{ ...numSx, color: 'text.disabled' }}>excl.</TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell sx={{ ...cellSx, fontWeight: 700, backgroundColor: theme.table.headerBg, borderBottom: 0 }}>
              Total demand
            </TableCell>
            <TableCell sx={{ ...numSx, backgroundColor: theme.table.headerBg, borderBottom: 0 }} />
            <TableCell sx={{ ...numSx, backgroundColor: theme.table.headerBg, borderBottom: 0 }} />
            <TableCell sx={{ ...numSx, fontWeight: 700, backgroundColor: theme.table.headerBg, borderBottom: 0, whiteSpace: 'nowrap' }}>
              {demand.demandPersonHours} h ≈ {demand.demandFteWeeks} FTE-wk
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
